import React, { useState, useEffect } from 'react';
import { Award, Target, ChevronDown, ChevronUp, Grid, List, Sparkles } from 'lucide-react';
import axios from 'axios';
import BadgeModal from './BadgeModal';
import './LevelSystem.css';

const BadgeDisplay = ({ user, showProgress = true, maxDisplay = 12, t }) => {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [viewMode, setViewMode] = useState('earned');

  useEffect(() => {
    if (user) {
      fetchBadgeData();
    }
  }, [user]);

  const fetchBadgeData = async () => {
    try {
      const response = await axios.get('/api/levels/my-badges');
      setBadgeData(response.data);
      setError(false);
    } catch (error) {
      console.error('Failed to fetch badge data:', error);
      setError(true);
      setBadgeData({
        earned: [],
        available: [],
        categories: {},
        nextAvailable: [],
        statistics: {
          totalEarned: 0,
          totalAvailable: 50,
          completionPercentage: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeClick = (badge, isProgress = false) => {
    setSelectedBadge({ ...badge, isProgress });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBadge(null);
  };

  const getBadgesToDisplay = () => {
    if (!badgeData) return [];

    let badges = [];

    switch (viewMode) {
      case 'earned':
        badges = badgeData.earned || [];
        break;
      case 'progress':
        badges = badgeData.nextAvailable || [];
        break;
      case 'all':
        badges = [
          ...(badgeData.earned || []),
          ...(badgeData.nextAvailable || []).slice(0, 8)
        ];
        break;
      default:
        badges = badgeData.earned || [];
    }

    if (selectedCategory !== 'all') {
      badges = badges.filter(badge => badge.category === selectedCategory);
    }

    if (!showAll && badges.length > maxDisplay) {
      return badges.slice(0, maxDisplay);
    }

    return badges;
  };

  const getCategories = () => {
    if (!badgeData) return [];

    const categories = new Set();

    [...(badgeData.earned || []), ...(badgeData.nextAvailable || [])].forEach(badge => {
      if (badge.category) {
        categories.add(badge.category);
      }
    });

    return Array.from(categories);
  };

  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      'win_streak': t ? t('winStreak', 'Win Streak') : 'Win Streak',
      'filler': t ? t('filler', 'Filler') : 'Filler',
      'total_wins': t ? t('totalWins', 'Total Wins') : 'Total Wins',
      'points_winning': t ? t('pointsWinning', 'Points Won') : 'Points Won',
      'points_purchase': t ? t('pointsPurchase', 'Points Bought') : 'Points Bought',
      'multiplayer': 'Multiplayer',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'MP Wins',
      'battle_royale_wins': 'BR Wins'
    };

    return categoryNames[category] || category;
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'legendary': return '#fbbf24';
      case 'epic': return '#8b5cf6';
      case 'rare': return '#3b82f6';
      default: return '#71717a';
    }
  };

  if (loading) {
    return (
      <div className="badge-display">
        <div className="badge-display-header">
          <div className="badge-display-title">
            <Award className="w-5 h-5" style={{ color: '#fbbf24' }} />
            <span>{t ? t('loadingBadges', 'Loading Badges...') : 'Loading Badges...'}</span>
          </div>
        </div>
        <div className="badges-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="badge-item badge-progress-item" style={{ opacity: 0.4 }}>
              <div className="badge-icon">🏅</div>
              <div className="badge-name">Loading...</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!badgeData) {
    return (
      <div className="badge-display">
        <div className="badge-display-header">
          <div className="badge-display-title">
            <Award className="w-5 h-5" style={{ color: '#fbbf24' }} />
            <span>{t ? t('badges', 'Badges') : 'Badges'}</span>
          </div>
          <div className="badge-count">0/50</div>
        </div>
        <div className="badges-grid">
          <div className="badge-item badge-progress-item">
            <div className="badge-icon">🏅</div>
            <div className="badge-name">{t ? t('noBadges', 'No badges yet') : 'No badges yet'}</div>
          </div>
        </div>
      </div>
    );
  }

  const badgesToDisplay = getBadgesToDisplay();
  const categories = getCategories();
  const totalBadges = badgeData.statistics?.totalAvailable || 50;
  const earnedBadges = badgeData.statistics?.totalEarned || 0;
  const completionPercentage = totalBadges > 0 ? (earnedBadges / totalBadges * 100) : 0;
  const totalBadgesCount = (viewMode === 'earned' ? badgeData.earned?.length :
                           viewMode === 'progress' ? badgeData.nextAvailable?.length :
                           (badgeData.earned?.length || 0) + Math.min(8, badgeData.nextAvailable?.length || 0)) || 0;

  return (
    <>
      <div className="badge-display">
        {/* Header */}
        <div className="badge-display-header">
          <div className="badge-display-title">
            <Award className="w-5 h-5" style={{ color: '#fbbf24' }} />
            <span>{t ? t('badges', 'Badges') : 'Badges'}</span>
            {error && (
              <span style={{
                color: '#ef4444',
                fontSize: '11px',
                marginLeft: '8px',
                padding: '2px 8px',
                background: 'rgba(239, 68, 68, 0.15)',
                borderRadius: '10px'
              }}>
                {t ? t('offline', 'Offline') : 'Offline'}
              </span>
            )}
          </div>
          <div className="badge-count">
            <Sparkles className="w-3 h-3" style={{ marginRight: '4px', display: 'inline' }} />
            {earnedBadges}/{totalBadges}
          </div>
        </div>

        {/* Filters Row */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* View Mode Tabs */}
          <div style={{
            display: 'flex',
            background: '#252532',
            borderRadius: '10px',
            padding: '4px',
            gap: '4px'
          }}>
            {[
              { id: 'earned', label: t ? t('earned', 'Earned') : 'Earned', count: badgeData.earned?.length || 0 },
              { id: 'progress', label: t ? t('inProgress', 'Progress') : 'Progress', count: badgeData.nextAvailable?.length || 0 },
              { id: 'all', label: t ? t('all', 'All') : 'All', count: null }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                style={{
                  background: viewMode === tab.id ? '#3f3f46' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: viewMode === tab.id ? '#fff' : '#71717a',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {tab.label}
                {tab.count !== null && (
                  <span style={{
                    background: viewMode === tab.id ? '#52525b' : '#3f3f46',
                    padding: '2px 6px',
                    borderRadius: '6px',
                    fontSize: '10px'
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                background: '#252532',
                border: '1px solid #3f3f46',
                borderRadius: '8px',
                color: '#fff',
                padding: '8px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="all">{t ? t('allCategories', 'All Categories') : 'All Categories'}</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {getCategoryDisplayName(category)}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Badges Grid */}
        <div className="badges-grid">
          {badgesToDisplay.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '40px 20px',
              color: '#71717a'
            }}>
              <Award style={{ width: 40, height: 40, marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: '14px', fontWeight: 500 }}>
                {viewMode === 'earned'
                  ? (t ? t('noBadgesEarned', 'No badges earned yet') : 'No badges earned yet')
                  : viewMode === 'progress'
                  ? (t ? t('allBadgesEarned', 'You\'ve earned all available badges!') : 'You\'ve earned all available badges!')
                  : (t ? t('noBadgesAvailable', 'No badges available') : 'No badges available')
                }
              </p>
              <p style={{ fontSize: '12px', marginTop: 8 }}>
                {viewMode === 'earned'
                  ? (t ? t('startPlaying', 'Start playing to unlock your first badge!') : 'Start playing to unlock your first badge!')
                  : ''
                }
              </p>
            </div>
          ) : (
            badgesToDisplay.map((badge, index) => {
              const isProgress = !badge.unlockedAt && badge.progressPercentage !== undefined;
              const isEarned = badge.unlockedAt || badge.badgeId;

              return (
                <div
                  key={badge.badgeId || badge.id || index}
                  className={`badge-item ${badge.rarity || 'common'} ${isProgress ? 'badge-progress-item' : ''}`}
                  onClick={() => handleBadgeClick(badge, isProgress)}
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  <div className="badge-icon">
                    {badge.icon || '🏅'}
                  </div>
                  <div className="badge-name">
                    {badge.badgeName || badge.name}
                  </div>

                  {/* Rarity indicator dot */}
                  {isEarned && badge.rarity && badge.rarity !== 'common' && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: getRarityColor(badge.rarity),
                      boxShadow: `0 0 6px ${getRarityColor(badge.rarity)}`
                    }} />
                  )}

                  {/* Progress bar for in-progress badges */}
                  {isProgress && (
                    <div
                      className="badge-progress-bar"
                      style={{ width: `${Math.min(100, badge.progressPercentage || 0)}%` }}
                    />
                  )}

                  {/* Progress percentage label */}
                  {isProgress && badge.progressPercentage !== undefined && (
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '9px',
                      fontWeight: 600,
                      color: '#22c55e',
                      background: 'rgba(34, 197, 94, 0.15)',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {Math.round(badge.progressPercentage)}%
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Show More/Less Button */}
        {totalBadgesCount > maxDisplay && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={() => setShowAll(!showAll)}
              style={{
                background: '#252532',
                border: '1px solid #3f3f46',
                color: '#a1a1aa',
                padding: '10px 20px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#2d2d3a';
                e.target.style.borderColor = '#52525b';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#252532';
                e.target.style.borderColor = '#3f3f46';
              }}
            >
              {showAll ? (
                <>
                  {t ? t('showLess', 'Show Less') : 'Show Less'}
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  {t ? t('showMore', 'Show More') : 'Show More'}
                  <span style={{
                    background: '#3f3f46',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '11px'
                  }}>
                    +{totalBadgesCount - maxDisplay}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Footer with Progress */}
        <div className="badges-footer">
          <div className="badges-completion">
            <span style={{ fontWeight: 600 }}>
              {Math.round(completionPercentage)}%
            </span>
            <div className="completion-bar">
              <div
                className="completion-progress"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <span style={{ fontSize: '11px' }}>
              {t ? t('complete', 'Complete') : 'Complete'}
            </span>
          </div>

          {badgeData.nextAvailable?.length > 0 && viewMode !== 'progress' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#22c55e'
            }}>
              <Target className="w-4 h-4" />
              <span>
                {badgeData.nextAvailable.length} {t ? t('inProgress', 'in progress') : 'in progress'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Badge Modal */}
      {showModal && selectedBadge && (
        <BadgeModal
          badge={selectedBadge}
          onClose={closeModal}
          t={t}
        />
      )}
    </>
  );
};

export default BadgeDisplay;
